import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  MenuItem,
  InputAdornment,
  Divider,
  Chip,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import { useNewVehicle } from "../common/hooks";
import apiUris from "../common/api/apiUris";
import {
  formatVehicleIdentifier,
  mergeCalculatedComtracValues,
} from "../common/constants/commonFunction";

const ComtracCalculationModal = ({ closeModal, selectedRows, companyId, dealerId, setSelectedRows }) => {
  const selectedVehicle =
    selectedRows && selectedRows.length > 0 ? selectedRows[0] : {};

  const { multipleNewVehicleApi } = useNewVehicle();

  // ─── Form fields ────────────────────────────────────────────────────────────
  const [customerName, setCustomerName] = useState("");
  const [customerRate, setCustomerRate] = useState(12);
  const [termMonths, setTermMonths] = useState(12);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [residualPercent, setResidualPercent] = useState(0);
  const [residualAmount, setResidualAmount] = useState(0);
  const [acquisitionFee, setAcquisitionFee] = useState(695);
  const [salesTax, setSalesTax] = useState(0);
  const [netTradeIn, setNetTradeIn] = useState(0);
  const [capCostReduction, setCapCostReduction] = useState(0);
  const [otherCapFees, setOtherCapFees] = useState(0);
  const [rebate, setRebate] = useState(0);
  const [notes, setNotes] = useState("");

  // ─── Preview details ─────────────────────────────────────────────────────────
  const [previewDetails, setPreviewDetails] = useState({
    estimatedMonthlyPayment: 0,
    baseMonthlyPayment: 0,
    monthlySalesTax: 0,
    totalCapCostReduction: 0,
    netCapCost: 0,
    residualAmountResult: 0,
  });

  // ─── Bind form fields to selected fleet whenever selection changes ────────────
  useEffect(() => {
    if (!selectedVehicle || Object.keys(selectedVehicle).length === 0) return;

    const cost = parseFloat(selectedVehicle.newVehicleCost || 0);
    const resPct = parseFloat(selectedVehicle.residualPercentage || 0);
    // Prefer stored resale value; fall back to cost × residual %
    const resAmt =
      parseFloat(selectedVehicle.newEstimatedResaleValue || 0) ||
      parseFloat((cost * (resPct / 100)).toFixed(2));

    setSellingPrice(cost);
    setResidualPercent(resPct);
    setResidualAmount(resAmt);
    if (selectedVehicle.termMonths) {
      setTermMonths(selectedVehicle.termMonths);
    } else if (selectedVehicle.years || selectedVehicle.proposedCycle) {
      setTermMonths((selectedVehicle.years || selectedVehicle.proposedCycle) * 12);
    }

    // Reset preview when fleet changes
    setPreviewDetails({
      estimatedMonthlyPayment: 0,
      baseMonthlyPayment: 0,
      monthlySalesTax: 0,
      totalCapCostReduction: 0,
      netCapCost: 0,
      residualAmountResult: 0,
    });
  }, [selectedRows]);

  // ─── Live lease-payment preview (updates as user types) ────────────────────
  useEffect(() => {
    const cost = parseFloat(sellingPrice) || 0;
    const resAmt = parseFloat(residualAmount) || 0;
    const term = parseInt(termMonths) || 12;
    const buyRate = parseFloat(customerRate) || 0;
    const taxRate = parseFloat(salesTax) || 0;
    const acqFee = parseFloat(acquisitionFee) || 0;
    const cashDown = parseFloat(capCostReduction) || 0;
    const rebateVal = parseFloat(rebate) || 0;
    const tradeIn = parseFloat(netTradeIn) || 0;
    const otherFees = parseFloat(otherCapFees) || 0;

    // Standard lease formula
    const totalCapReduction = cashDown + rebateVal + tradeIn;
    const netCap = cost + acqFee + otherFees - totalCapReduction;
    const moneyFactor = buyRate / 2400;                          // APR → money factor
    const monthlyDepr = term > 0 ? (netCap - resAmt) / term : 0;
    const monthlyFinance = (netCap + resAmt) * moneyFactor;
    const baseMonthly = monthlyDepr + monthlyFinance;
    const monthlySalesTaxV = baseMonthly * (taxRate / 100);
    const totalMonthly = baseMonthly + monthlySalesTaxV;

    const safe = (v) => (isFinite(v) && v >= 0 ? v : 0);

    setPreviewDetails({
      estimatedMonthlyPayment: safe(totalMonthly),
      baseMonthlyPayment: safe(baseMonthly),
      monthlySalesTax: safe(monthlySalesTaxV),
      totalCapCostReduction: safe(totalCapReduction),
      netCapCost: safe(netCap),
      residualAmountResult: safe(resAmt),
    });
  }, [
    sellingPrice, residualAmount, termMonths, customerRate,
    salesTax, acquisitionFee, capCostReduction, rebate, netTradeIn, otherCapFees,
  ]);

  // ─── Sync database helpers ───────────────────────────────────────────────────
  const updateResidualInDb = async (pct) => {
    if (!selectedRows || selectedRows.length === 0) return;

    const finalPayload = {
      updateIds: selectedRows.map((row) => {
        const newResPct = parseFloat(pct) > 0 ? parseFloat(pct) : row.residualPercentage;
        const cost = parseFloat(sellingPrice) > 0 ? parseFloat(sellingPrice) : row.newVehicleCost;
        return {
          id: row.newVehicleId,
          ...(newResPct != null && { residualPercentage: newResPct }),
          ...(cost != null && { newVehicleCost: cost }),
        };
      }),
      companyId: companyId,
      dealerId: dealerId,
    };

    try {
      await multipleNewVehicleApi({
        values: finalPayload,
      });
    } catch (err) {
      console.error("Failed to update residual in DB:", err);
    }
  };

  const saveCalculateResultsToDb = async (cv, ds) => {
    if (!selectedRows || selectedRows.length === 0) return;

    // Prefer values returned by the calculation (cv / ds). Fall back to
    // locally-entered form values where necessary.
    const resolvedPaymentAmt = (cv?.totalMonthlyPayment ?? cv?.monthlyPayment) ||
      (previewDetails.estimatedMonthlyPayment > 0 ? previewDetails.estimatedMonthlyPayment : null);
    const resolvedResPct = parseFloat(residualPercent) > 0 ? parseFloat(residualPercent) : null;
    const resolvedResidualAmt = (ds?.residualAmount != null && ds?.residualAmount > 0)
      ? ds.residualAmount
      : (parseFloat(residualAmount) > 0 ? parseFloat(residualAmount) : null);

    // Net capitalized cost returned by API is the preferred source for
    // what should be stored as `newVehicleCost`.
    const resolvedNetCap = (cv?.netCapitalizedCost ?? previewDetails.netCapCost) ||
      (parseFloat(sellingPrice) > 0 ? parseFloat(sellingPrice) : null);

    const resolvedTermMonths = parseInt(termMonths) > 0 ? parseInt(termMonths) : null;
    const resolvedYears = resolvedTermMonths ? Math.round(resolvedTermMonths / 12) : null;

    // Build per-row payload and compute equityGain as:
    //   equityGain = newEstimatedResaleValue - residualAmount
    // where residualAmount is either provided directly or derived from
    // newVehicleCost * (residualPercentage / 100).
    const finalPayload = {
      updateIds: selectedRows.map((row) => {
        const rowResale = row.newEstimatedResaleValue != null ? parseFloat(row.newEstimatedResaleValue) : null;
        const rowCost = resolvedNetCap != null ? parseFloat(resolvedNetCap) : (row.newVehicleCost != null ? parseFloat(row.newVehicleCost) : null);
        const rowResPct = resolvedResPct != null ? resolvedResPct : (row.residualPercentage != null ? parseFloat(row.residualPercentage) : null);
        const residualAmtCalculated = resolvedResidualAmt != null ? parseFloat(resolvedResidualAmt) : (rowCost != null && rowResPct != null ? parseFloat((rowCost * (rowResPct / 100)).toFixed(2)) : null);

        const equity = rowResale != null && residualAmtCalculated != null ? parseFloat((rowResale - residualAmtCalculated).toFixed(2)) : null;

        return {
          id: row.newVehicleId || row.id,
          ...(resolvedResPct != null && { residualPercentage: resolvedResPct }),
          ...(resolvedPaymentAmt != null && { paymentAmount: resolvedPaymentAmt }),
          ...(resolvedNetCap != null && { newVehicleCost: resolvedNetCap }),
          ...(rowResale != null && { newEstimatedResaleValue: rowResale }),
          ...(equity != null && { equityGain: equity }),
          ...(resolvedTermMonths != null && { termMonths: resolvedTermMonths }),
          ...(resolvedYears != null && { years: resolvedYears, proposedCycle: resolvedYears }),
        };
      }),
      companyId: companyId,
      dealerId: dealerId,
    };

    try {
      await multipleNewVehicleApi({
        values: finalPayload,
      });

      if (typeof setSelectedRows === "function") {
        setSelectedRows((currentRows = []) =>
          mergeCalculatedComtracValues(currentRows, {
            totalMonthlyPayment: resolvedPaymentAmt,
            monthlyPayment: previewDetails.baseMonthlyPayment,
            salesTax: previewDetails.monthlySalesTax,
            netCapitalizedCost: resolvedNetCap,
            residualAmount: resolvedResidualAmt,
            residualPercentage: resolvedResPct,
            termMonths: resolvedTermMonths,
            dealStructure: {
              residualAmount: resolvedResidualAmt,
              termMonths: resolvedTermMonths,
            },
          })
        );
      }
      // closeModal is called by handleCalculate after this resolves
    } catch (err) {
      console.error("Failed to save calculation results to DB:", err);
    }
  };

  const handleResidualBlur = () => {
    updateResidualInDb(residualPercent);
  };

  // ─── Residual % ↔ amount sync ────────────────────────────────────────────────
  const handleResidualPercentChange = (e) => {
    const raw = e.target.value;
    setResidualPercent(raw);
    const pct = parseFloat(raw) || 0;
    setResidualAmount(parseFloat((parseFloat(sellingPrice || 0) * (pct / 100)).toFixed(2)));
  };

  const handleResidualAmountChange = (e) => {
    const raw = e.target.value;
    setResidualAmount(raw);
    const amt = parseFloat(raw) || 0;
    const cost = parseFloat(sellingPrice) || 0;
    if (cost > 0) {
      setResidualPercent(parseFloat(((amt / cost) * 100).toFixed(2)));
    }
  };

  // Keep residual amount in sync when selling price changes
  const handleSellingPriceChange = (e) => {
    const raw = e.target.value;
    setSellingPrice(raw);
    const cost = parseFloat(raw) || 0;
    const pct = parseFloat(residualPercent) || 0;
    if (pct > 0) {
      setResidualAmount(parseFloat((cost * (pct / 100)).toFixed(2)));
    }
  };

  // ─── Clear ────────────────────────────────────────────────────────────────────
  const handleClear = () => {
    setCustomerName("");
    setCustomerRate(12);
    setTermMonths(12);
    setSellingPrice(0);
    setResidualPercent(0);
    setResidualAmount(0);
    setAcquisitionFee(695);
    setSalesTax(0);
    setNetTradeIn(0);
    setCapCostReduction(0);
    setOtherCapFees(0);
    setRebate(0);
    setNotes("");
    setPreviewDetails({
      estimatedMonthlyPayment: 0,
      baseMonthlyPayment: 0,
      monthlySalesTax: 0,
      totalCapCostReduction: 0,
      netCapCost: 0,
      residualAmountResult: 0,
    });
  };

  // ─── Calculate ────────────────────────────────────────────────────────────────
  // The Ally API (secure.ally.com) enforces CORS and cannot be called directly
  // from the browser. The request is sent to the backend proxy endpoint which
  // forwards it server-side to Ally. If the proxy returns data, those values are
  // used; otherwise the locally-computed lease preview is used as a fallback.
  const handleCalculate = async () => {
    const payload = {
      vehicleDetails: {
        applicationId: customerName || "",
        vin: selectedVehicle.vin || "",
        year: parseInt(selectedVehicle.newYear || selectedVehicle.year || 0),
        make: selectedVehicle.newMake || selectedVehicle.make || "",
        model: selectedVehicle.newModel || selectedVehicle.model || "",
        leaseDate: null,
      },
      dealStructure: {
        termMonths: parseInt(termMonths) || 12,
        buyRate: parseFloat(customerRate) || 0,
        dealerReserveRate: 0,
        vehicleSellingPrice: parseFloat(sellingPrice) || 0,
        taxRate: parseFloat(salesTax) || 0,
        cashDown: parseFloat(capCostReduction) || 0,
        rebate: parseFloat(rebate) || 0,
        grossTradeIn: parseFloat(netTradeIn) || 0,
        payoff: 0,
        residualAmount: parseFloat(residualAmount) || 0,
      },
      taxesAndFees: {
        titleLicenseRegistrationFees: 0,
        titleLicenseRegistrationFeesCapitalized: false,
        tireTaxFee: 0,
        tireTaxFeeCapitalized: false,
        otherCapitalizedFees:
          (parseFloat(acquisitionFee) || 0) + (parseFloat(otherCapFees) || 0),
        capitalizedTax: 0,
        capitalReductionTax: 0,
        personalPropertyTaxUsState: "Not Applicable",
        federalTaxExempted: false,
      },
      aftermarketProducts: {
        gapToDealer: 0,
        maintenance: 0,
        mechanicalServiceContract: 0,
      },
    };

    try {
      // ── Route through backend proxy (avoids CORS) ──────────────────────
      const response = await axios.post(
        apiUris.comtracApi.calculate,
        payload
      );

      if (response.data) {
        const cv = response.data.calculatedValues || {};
        const ds = response.data.dealStructure || {};
        setPreviewDetails({
          estimatedMonthlyPayment: cv.totalMonthlyPayment ?? 0,
          baseMonthlyPayment: cv.monthlyPayment ?? 0,
          monthlySalesTax: cv.salesTax ?? 0,
          totalCapCostReduction: cv.totalCapitalizedCostReduction ?? 0,
          netCapCost: cv.netCapitalizedCost ?? 0,
          residualAmountResult: ds.residualAmount ?? parseFloat(residualAmount) ?? 0,
        });
        await saveCalculateResultsToDb(cv, ds);
        closeModal();
        toast.success("Quote calculated successfully.");
      }
    } catch (error) {
      console.error("Comtrac API call failed:", error);

      // ── Fallback: use locally-computed lease values ─────────────────────
      // This runs if the backend proxy endpoint is not yet implemented.
      // Remove this fallback once the backend /Comtrac/Calculate route is live.
      const cv = {
        totalMonthlyPayment: previewDetails.estimatedMonthlyPayment,
        monthlyPayment: previewDetails.baseMonthlyPayment,
        salesTax: previewDetails.monthlySalesTax,
        totalCapitalizedCostReduction: previewDetails.totalCapCostReduction,
        netCapitalizedCost: previewDetails.netCapCost,
      };
      const ds = {
        residualAmount: previewDetails.residualAmountResult || parseFloat(residualAmount) || 0,
      };

      try {
        await saveCalculateResultsToDb(cv, ds);

        if (typeof setSelectedRows === "function") {
          setSelectedRows((currentRows = []) =>
            mergeCalculatedComtracValues(currentRows, {
              totalMonthlyPayment: cv.totalMonthlyPayment ?? previewDetails.estimatedMonthlyPayment,
              monthlyPayment: cv.monthlyPayment ?? previewDetails.baseMonthlyPayment,
              salesTax: cv.salesTax ?? previewDetails.monthlySalesTax,
              netCapitalizedCost: cv.netCapitalizedCost ?? previewDetails.netCapCost,
              residualAmount: (ds.residualAmount ?? previewDetails.residualAmountResult ?? parseFloat(residualAmount)) || 0,
              residualPercentage: parseFloat(residualPercent) || 0,
              termMonths: parseInt(termMonths) || 12,
              dealStructure: ds,
            })
          );
        }

        closeModal();
        toast.success("Quote calculated using local estimate (Ally API unavailable).");
      } catch (dbErr) {
        console.error("Failed to save calculation results:", dbErr);
        toast.error("Failed to save quote. Please try again.");
      }
    }
  };

  // ─── Download PDF ─────────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("ComTRAC Quick Quote", 20, 20);

    doc.setFontSize(12);
    doc.text(`Vehicle: ${formatVehicleIdentifier(selectedVehicle.fleetNo) || "-"}`, 20, 30);
    doc.text(`Customer Name: ${customerName}`, 20, 40);
    doc.text(`Customer Rate: ${customerRate}%`, 20, 50);
    doc.text(`Term: ${termMonths} months`, 20, 60);
    doc.text(`Total Selling Price: $${sellingPrice}`, 20, 70);
    doc.text(`Residual: ${residualPercent}% / $${residualAmount}`, 20, 80);
    doc.text(`Acquisition Fee: $${acquisitionFee}`, 20, 90);
    doc.text(`Monthly Sales/Use Tax: ${salesTax}%`, 20, 100);
    doc.text(`Net Trade-In: $${netTradeIn}`, 20, 110);
    doc.text(`Cap Cost Reduction: $${capCostReduction}`, 20, 120);
    doc.text(`Other Cap Fees: $${otherCapFees}`, 20, 130);
    doc.text(`Rebate: $${rebate}`, 20, 140);

    doc.setFontSize(14);
    doc.text("Lease Preview Details:", 20, 155);
    doc.setFontSize(12);
    doc.text(
      `Estimated Monthly Payment: $${parseFloat(previewDetails.estimatedMonthlyPayment || 0).toFixed(2)}`,
      20,
      165
    );
    doc.text(
      `Base Monthly Payment: $${parseFloat(previewDetails.baseMonthlyPayment || 0).toFixed(2)}`,
      20,
      175
    );
    doc.text(
      `Monthly Sales Tax: $${parseFloat(previewDetails.monthlySalesTax || 0).toFixed(2)}`,
      20,
      185
    );
    doc.text(
      `Total Cap Cost Reduction: $${parseFloat(previewDetails.totalCapCostReduction || 0).toFixed(2)}`,
      20,
      195
    );
    doc.text(
      `Net Cap Cost: $${parseFloat(previewDetails.netCapCost || 0).toFixed(2)}`,
      20,
      205
    );
    doc.text(
      `Residual Amount: $${parseFloat(previewDetails.residualAmountResult || 0).toFixed(2)}`,
      20,
      215
    );

    if (notes) {
      doc.text(`Notes: ${notes}`, 20, 225);
    }

    doc.save(`Comtrac_Quote_${selectedVehicle.fleetNo || customerName || "Draft"}.pdf`);
  };

  const fmt = (val) =>
    `$${parseFloat(val || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxHeight: "85vh", overflowY: "auto", display: "flex", flexDirection: "column", paddingTop: 0, paddingBottom: 0 }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1.3 }}>
          ComTRAC Quick Quote
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Enter the details and download the quote as a PDF.
        </Typography>
      </Box>

      {/* ── Selected Vehicle(s) chips ───────────────────────────────────── */}
      <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        <Typography variant="caption" sx={{ fontWeight: "bold", mr: 0.5 }}>
          Selected Vehicle(s):
        </Typography>
        {selectedRows?.map((row) => (
          <Chip
            key={row.id || row.fleetNo}
            label={`${formatVehicleIdentifier(row.fleetNo)}${row.annualMileage != null ? ` - ${Number(row.annualMileage).toLocaleString()} mi` : ""}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
        ))}
      </Box>

      <Grid container spacing={2}>
        {/* ── Left Form Side ─────────────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={1.5}>

            {/* Row 1: Customer name | Rate | Term */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Customer name"
                fullWidth
                variant="standard"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Customer rate (required)"
                fullWidth
                variant="standard"
                type="number"
                value={customerRate}
                onChange={(e) => setCustomerRate(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Term in months (required)"
                fullWidth
                variant="standard"
                value={termMonths}
                onChange={(e) => setTermMonths(e.target.value)}
              >
                {[12, 24, 36, 48, 60].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 2: Selling price | Residual */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Total selling price (required)"
                fullWidth
                variant="standard"
                type="number"
                value={sellingPrice}
                onChange={handleSellingPriceChange}
                onBlur={handleResidualBlur}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={1} alignItems="flex-end">
                <TextField
                  label="Residual (required)"
                  variant="standard"
                  fullWidth
                  type="number"
                  value={residualPercent}
                  onChange={handleResidualPercentChange}
                  onBlur={handleResidualBlur}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
                <Typography variant="body2" sx={{ mb: 1, whiteSpace: "nowrap" }}>
                  or
                </Typography>
                <TextField
                  variant="standard"
                  fullWidth
                  type="number"
                  value={residualAmount}
                  onChange={handleResidualAmountChange}
                  onBlur={handleResidualBlur}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Box>
            </Grid>

            {/* Row 3: Acquisition fee */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Acquisition fee"
                fullWidth
                variant="standard"
                type="number"
                value={acquisitionFee}
                onChange={(e) => setAcquisitionFee(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Advanced info header */}
            <Grid item xs={12}>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", color: "#1976d2", cursor: "pointer" }}
              >
                Add advanced info
              </Typography>
            </Grid>

            {/* Row 4: Monthly sales/use tax | Net trade-in */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Monthly sales/use tax"
                fullWidth
                variant="standard"
                type="number"
                value={salesTax}
                onChange={(e) => setSalesTax(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Net trade-in"
                fullWidth
                variant="standard"
                type="number"
                value={netTradeIn}
                onChange={(e) => setNetTradeIn(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Row 5: Cap cost reduction | Other cap fees */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Cap cost reduction"
                fullWidth
                variant="standard"
                type="number"
                value={capCostReduction}
                onChange={(e) => setCapCostReduction(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Other cap fees"
                fullWidth
                variant="standard"
                type="number"
                value={otherCapFees}
                onChange={(e) => setOtherCapFees(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Row 6: Rebate */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Rebate"
                fullWidth
                variant="standard"
                type="number"
                value={rebate}
                onChange={(e) => setRebate(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12} sm={9}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={1}
                variant="filled"
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>

            {/* Action buttons */}
            <Grid item xs={12} display="flex" gap={1.5} mt={1} mb={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleCalculate}
              >
                Calculate Quote
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleDownloadPDF}
              >
                Download Quote (PDF)
              </Button>
              <Button variant="text" color="primary" size="small" onClick={handleClear}>
                Clear
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {/* ── Right Preview Side ──────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#F0F4F8", borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Lease preview details.
              </Typography>

              {/* Big payment figure */}
              <Box sx={{ mb: 1, pb: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <Typography variant="caption" color="textSecondary">
                  Estimated monthly payment
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                  {fmt(previewDetails.estimatedMonthlyPayment)}
                  <span style={{ fontSize: "12px", fontWeight: "normal", marginLeft: 2 }}>/mo</span>
                </Typography>
              </Box>

              {/* Compact flat list of remaining values */}
              {[
                { label: "Base monthly payment", value: previewDetails.baseMonthlyPayment },
                { label: "Monthly sales tax", value: previewDetails.monthlySalesTax },
                { label: "Total cap cost reduction", value: previewDetails.totalCapCostReduction },
                { label: "Net cap cost", value: previewDetails.netCapCost },
                { label: "Residual amount", value: previewDetails.residualAmountResult },
              ].map(({ label, value }) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.5,
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography variant="caption" color="textSecondary" sx={{ flexShrink: 0, mr: 1 }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {fmt(value)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComtracCalculationModal;
